import type { FronteggUserEntry, UserRow } from "../../api/userVerification.service.js";
import { UserStatus, type UserStatusValue } from "./constants.js";

const validStatuses = new Set<string>(Object.values(UserStatus));

function parseUserStatus(status: string | undefined | null): UserStatusValue {
  if (status && validStatuses.has(status)) {
    return status as UserStatusValue;
  }
  return UserStatus.UNVERIFIED;
}

type StatusDisplay = {
  label: string;
  color: "success" | "warning" | "error";
};

const statusDisplayMap: Record<UserStatusValue, StatusDisplay> = {
  [UserStatus.VERIFIED]: { label: "Verified", color: "success" },
  [UserStatus.UNVERIFIED]: { label: "Unverified", color: "warning" },
  [UserStatus.REJECTED]: { label: "Rejected", color: "error" },
};

export function getStatusDisplay(status: UserStatusValue): StatusDisplay {
  return statusDisplayMap[status];
}

export type UserVerificationEntry = {
  id: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  companyWebsite: string;
  submittedFirmName: string;
  firmName: string;
  firmRef: string;
  status: UserStatusValue;
  requestDate: string;
};

export function fromUserRow(user: UserRow): UserVerificationEntry {
  const nameParts = (user.name || "").trim().split(/\s+/);
  const firstName = nameParts[0] ?? "";
  const lastName = nameParts.slice(1).join(" ");

  const metadata = user.metadata ?? {};
  const requestDate = user.createdAt ? new Date(user.createdAt).toISOString().slice(0, 10) : "";

  const status = parseUserStatus(user.status);

  return {
    id: user.externalId ?? user.id,
    tenantId: String(metadata.tenantId ?? ""),
    firstName,
    lastName,
    email: user.email,
    companyWebsite: String(metadata.companyUrl ?? ""),
    submittedFirmName: String(metadata.companyName ?? ""),
    firmName: "",
    firmRef: "",
    status,
    requestDate,
  };
}

export function toUserVerificationEntry(user: FronteggUserEntry): UserVerificationEntry {
  const nameParts = (user.name || "").trim().split(/\s+/);
  const firstName = nameParts[0] ?? "";
  const lastName = nameParts.slice(1).join(" ");

  let metadata: Record<string, unknown> = {};
  if (user.metadata) {
    try {
      metadata = JSON.parse(user.metadata) as Record<string, unknown>;
    } catch {
      // malformed metadata — leave empty
    }
  }

  const requestDate = user.createdAt ? new Date(user.createdAt).toISOString().slice(0, 10) : "";

  const rejectedRole = !!user.accounts.find(
    account =>
      !!account.roles.find(
        role => role.name.toLowerCase().replaceAll(" ", "_") === UserStatus.REJECTED,
      ),
  );

  return {
    id: user.id,
    tenantId: user.tenantId,
    firstName,
    lastName,
    email: user.email,
    companyWebsite: String(metadata.requestedCompanyUrl ?? ""),
    submittedFirmName: String(metadata.requestedCompanyName ?? ""),
    firmName: String(metadata.firm_name ?? ""),
    firmRef: String(metadata.firm_ref ?? ""),
    status: rejectedRole ? UserStatus.REJECTED : UserStatus.UNVERIFIED,
    requestDate,
  };
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  if (typeof (error as Error & { status?: unknown }).status !== "number") return fallback;
  const message = error.message.trim();
  if (message.length === 0 || /^HTTP \d+$/.test(message)) return fallback;
  return message;
}
